import { centroid } from '../geometry.js';

/**
 * Grid dispersal pattern.
 *
 * Arranges markers in a compact rectangular grid centered on their geometric
 * mean. The grid dimensions are chosen to minimize the maximum dimension
 * (e.g., 2x2 for 4 markers, 3x2 for 5 markers).
 * Markers are assigned to the closest grid slot using Manhattan distance
 * to minimize how far they are shifted.
 *
 * @param {import('../DisperseEngine.js').MarkerDescriptor[]} group
 * @param {number} spacing
 * @returns {Map<string|number, import('../DisperseEngine.js').MarkerOffset>}
 */
export function gridPattern(group, spacing) {
  const offsets = new Map();
  const N = group.length;

  if (N === 0) {
    return offsets;
  }

  if (N === 1) {
    offsets.set(group[0].id, { dx: 0, dy: 0 });
    return offsets;
  }

  const center = centroid(group.map((m) => ({ x: m.x, y: m.y })));

  // Grid spacing must accommodate the largest marker in each dimension
  const maxW = Math.max(...group.map((m) => m.width));
  const maxH = Math.max(...group.map((m) => m.height));
  const gridSpacingX = maxW + spacing;
  const gridSpacingY = maxH + spacing;

  // Determine grid dimensions
  const cols = Math.ceil(Math.sqrt(N));
  const rows = Math.ceil(N / cols);

  // Generate slots
  const rawSlots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      rawSlots.push({
        x: c * gridSpacingX,
        y: r * gridSpacingY,
      });
    }
  }

  // Center slots around the centroid
  const slotCenter = centroid(rawSlots);
  const slots = rawSlots.map((s) => ({
    x: center.x + (s.x - slotCenter.x),
    y: center.y + (s.y - slotCenter.y),
  }));

  // Greedily assign markers to the closest slot using Manhattan distance
  const unassignedMarkers = new Set(group.map((m) => m.id));
  const unassignedSlots = new Set(slots.map((_, i) => i));

  const pairs = [];
  for (const m of group) {
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      const dist = Math.abs(m.x - s.x) + Math.abs(m.y - s.y);
      pairs.push({ markerId: m.id, slotIdx: i, dist });
    }
  }

  // Sort pairs by shortest distance
  pairs.sort((a, b) => a.dist - b.dist);

  for (const pair of pairs) {
    if (unassignedMarkers.has(pair.markerId) && unassignedSlots.has(pair.slotIdx)) {
      unassignedMarkers.delete(pair.markerId);
      unassignedSlots.delete(pair.slotIdx);
      const slot = slots[pair.slotIdx];
      const marker = group.find((m) => m.id === pair.markerId);
      offsets.set(marker.id, {
        dx: slot.x - marker.x,
        dy: slot.y - marker.y,
      });
    }
  }

  // Shift all offsets so that the centroid of the assigned slots exactly matches the original centroid.
  // This prevents the grid from looking off-center when not all slots are filled.
  const assignedPositions = group.map((m) => ({
    x: m.x + offsets.get(m.id).dx,
    y: m.y + offsets.get(m.id).dy,
  }));
  const dispersedCenter = centroid(assignedPositions);

  const driftX = center.x - dispersedCenter.x;
  const driftY = center.y - dispersedCenter.y;

  for (const m of group) {
    const off = offsets.get(m.id);
    off.dx += driftX;
    off.dy += driftY;
  }

  return offsets;
}
